import { useSignupPage, signupErrorReason } from 'utils/signupAnalytics';
import { sessionStorage as safeSessionStorage } from 'utils/browserStorage';
import React, {useState, useEffect, useRef} from 'react'
import {useHistory, useLocation} from 'react-router';
import {
    Button,
    MenuItem,
} from "@material-ui/core";

import {
    Page,
    Container,
    ChangeRoleBlock
} from "../SignUp1_DO/SignUp1_DO.styles";
import MaterialInput from "../../../components/Inputs/MaterialInput";
import {MaterialSelector} from "../../../components/Selector";
import Api, {config} from "../../../api";
import {completeRegistrationDoProps} from "../SignUp1_DO/SignUp1_DO";
import SignUpHeader from "../../../components/SignUpHeader";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import {usePersistedState} from "../../../hooks";
import entities from "../../../reduxStore/entityKeys";
import FixedLoader from "../../../components/FixedLoader/FixedLoader";
import useActionApi from "../../../hooks/useActionApi";
import { getSoftwareOptions } from "../../../utils/dentalSoftware";
import { trackSignUpComplete, UserType } from '../../../utils/facebookPixel';
import { 
    trackSignUpComplete as gtmTrackSignUpComplete,
    UserType as GTMUserType
} from '../../../utils/gtmDataLayer';
import { getMarketingAttribution, getAcquisitionSnapshotPayload } from '../../../utils/marketingAttribution';
import { getOfficeAddressValidationError } from '../SignUp1_DO/officeRegistrationAddress';
import { normalizeSignupText } from '../../../utils/signupText';

// @ts-ignore
const TempStars: any = window.TempStars;
// @ts-ignore
const app = window.app;

interface ErrorProps {
    primaryContact: string,
    parking: string,
    radiography: string,
    ultrasonic: string,
    avgApptTime: string,
    charting: string,
    software: string,
    scentFreePolicy: string,
}
interface completeRegistrationDo2Props {
    primaryContact: string,
    parking: string,
    radiography: string,
    ultrasonic: string,
    avgApptTime: string,
    charting: string,
    software: string,
    scentFreePolicy: string,
}

const SCENT_POLICY_OPTIONS = [
    { value: 'scent-free', label: 'Scent-free' },
    { value: 'not-scent-free', label: 'Not scent-free' },
];

export default function SignUp2_DO() {
    const history = useHistory();
    const signup = useSignupPage('office_details', 'office');
    const { state } = useLocation<completeRegistrationDoProps>();
    const {
        practiceName = '',
        ownersName = '',
        phone,
        country = 1,
        autocomplete,
        addressInput = '',
        photoUrl,
        marketingSource = '',
        otherSource = '',
        dsoAffiliation = '',
    } = state || {};
    const [screenError, setScreenError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<ErrorProps | undefined>();
    let user = TempStars.User.getCurrentUser();
    const analyticsTracked = useRef(false);
    const softwareOptions = getSoftwareOptions(country);

    const setUser = usePersistedState({
        entityName: entities.user,
    })[1];

    useEffect(() => {
        if (!analyticsTracked.current) {
            TempStars.Analytics.track('view_office_createAccount', {
                country: country === 0 ? 'CA' : 'US',
                practiceName: practiceName || ''
            });
            TempStars.Analytics.track('sign-up - office details viewed', {
                signup_flow: 'office',
                signup_step_number: 4,
                signup_step_name: 'office_details',
                country: country === 0 ? 'CA' : 'US',
                has_marketing_source: !!marketingSource,
                has_dso_affiliation: !!dsoAffiliation,
            });
            analyticsTracked.current = true;
        }
    }, [country, practiceName, marketingSource, dsoAffiliation]);

    useEffect(() => {
        const addressError = getOfficeAddressValidationError(
            addressInput || autocomplete?.formattedAddress || autocomplete?.address,
            autocomplete,
        );

        if (!autocomplete || addressError) {
            history.push({pathname: 'SignUp1_DO'}, {
                ...(state || {}),
                addressError,
            });
        }
    }, [addressInput, autocomplete, history, state]);

    const completeRegistration = async ({primaryContact, parking, radiography, ultrasonic, avgApptTime, charting, software, scentFreePolicy}: completeRegistrationDo2Props) => {
        app.showPreloader('Setting Up Account');
        TempStars.Analytics.track( 'sign-up - complete dental office details form');

        const addressError = getOfficeAddressValidationError(
            addressInput || autocomplete?.formattedAddress || autocomplete?.address,
            autocomplete,
        );
        if (!autocomplete || addressError) {
            app.hidePreloader();
            signup.error('invalid_address');
            history.push({pathname: 'SignUp1_DO'}, {
                ...(state || {}),
                addressError,
            });
            return;
        }

        const dentist = {
            frustrations: '', // What is your current biggest problem with temping and staffing...
            country: country,
            isComplete: 1,
        }

        // Format phone number as string (backend expects string, not number)
        const phoneString = typeof phone === 'number' ? phone.toString() : phone;

        const location = {
            email: user.email,
            secondaryEmail: '',
            accountingEmail: '',

            practiceName: normalizeSignupText(practiceName),
            businessOwner: normalizeSignupText(ownersName),
            country: country,
            address: autocomplete.address,
            city: autocomplete.city,
            province: autocomplete.state,
            postalCode: autocomplete.postalCode,
            phone: phoneString,
            photoUrl: photoUrl || '',
            avgApptTime: avgApptTime,
            charting: charting,
            parking: parking,
            primaryContact: normalizeSignupText(primaryContact),
            radiography: radiography,
            software: software,
            ultrasonic: ultrasonic,
            isCardValid: 0,
            isUpdated: 1,
            marketingSource: normalizeSignupText(otherSource) || normalizeSignupText(marketingSource),
            dsoAffiliation: normalizeSignupText(dsoAffiliation).toLowerCase() !== 'no' ? normalizeSignupText(dsoAffiliation) || null : null,
            scentFreePolicy,
            // T2-6448: first-touch marketing attribution captured on landing.
            // The API sanitizes/truncates these before persisting to Location.
            ...getMarketingAttribution(),
        }

        let officeSaved = false;
        try {
            TempStars.StripePubKey = config.stripe[country ? 'US' : 'CA'].pubKey
            const { locationId, registrationReceipt } = await Api.createDentist({dentist, location, acquisition: getAcquisitionSnapshotPayload()});
            officeSaved = true;
            signup.submitted();
            if (registrationReceipt && (window as any).TempStarsAttribution) {
                try { (window as any).TempStarsAttribution.complete(window.localStorage, registrationReceipt.id); } catch (_) {}
            }
            await TempStars.User.refresh()
            TempStars.Push.init();
            user = TempStars.User.getCurrentUser()
            TempStars.App.sendAnalytics(user);
            await TempStars.User.updateRegistration()
            await TempStars.User.reLogIn()
            TempStars.Analytics.track('sign-up - complete profile');
            
            // Track Dental Office sign-up completion (conversion event)
            // Parse owner name into first/last name if possible
            const nameParts = ownersName.split(' ');
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ') || '';
            
            // Facebook Pixel (existing - with Advanced Matching)
            await trackSignUpComplete({
                userType: UserType.DENTAL_OFFICE,
                userData: {
                    email: user.email,
                    phone: phone,
                    firstName: firstName,
                    lastName: lastName,
                    country: country, // Pass numeric country (0=CA, 1=US)
                    province: autocomplete?.state,
                    city: autocomplete?.city,
                }
            });
            
            // GTM dataLayer (new - NO PII, only IDs)
            gtmTrackSignUpComplete({
                userId: user.id,
                userType: GTMUserType.DENTAL_OFFICE,
                userRole: 'office_admin',
                country: country === 0 ? 'CA' : 'US',
                province: autocomplete?.state,
                city: autocomplete?.city,
                locationId: locationId,
            });
            
            TempStars.App.loadMenu()

            app.hidePreloader();

            await Api.updateToken()
            setUser(user)
            history.push({ 
                pathname: 'Onboarding_DO'
            }, {practiceName: location.practiceName, country});
        } catch (e: any) {
            // Refresh/login/conversion failures after a committed save are not page-save failures.
            if (!officeSaved) signup.error(signupErrorReason(e));
            console.error('Error creating dentist:', e);
            
            let errorMsg = 'An error occurred during registration. Please try again.';
            
            if (e.message && e.message.includes('not currently available')) {
                errorMsg = 'We are not currently available in your state. Please go back and select a different address.';
            } else if (e.message) {
                try {
                    const parsedMsg = JSON.parse(e.message);
                    if (parsedMsg.error && parsedMsg.error.message) {
                        errorMsg = parsedMsg.error.message;
                    }
                } catch (parseError) {
                    // Message is not JSON
                    errorMsg = e.message;
                }
            }
            
            TempStars.Analytics.track( 'sign-up - error', { errorReason: e });
            app.hidePreloader();
            
            setErrorMessage({
                primaryContact: '',
                parking: '',
                radiography: '',
                ultrasonic: '',
                avgApptTime: '',
                charting: '',
                software: errorMsg,
                scentFreePolicy: '',
            });
            setScreenError(true);
        }
    }

    const onSubmit = async (event: any) => {
        event.preventDefault()
        const {primaryContact, parking, radiography, ultrasonic, avgApptTime, charting, software, scentFreePolicy} = event.target
        let errors = {}

        if (!normalizeSignupText(primaryContact.value)) {
            errors = {...errors, primaryContact: 'Please enter Primary Contact'};
        }
        if (!parking.value.length) {
            errors = {...errors, parking: "Please choose parking"};
        }
        if (!radiography.value.length) {
            errors = {...errors, radiography: 'Please choose your radiography type'};
        }
        if (!ultrasonic.value.length) {
            errors = {...errors, ultrasonic: 'Please choose your ultrasonic type'};
        }
        if (!avgApptTime.value.length) {
            errors = {...errors, avgApptTime: 'Please choose your average recall time'};
        }
        if (!charting.value.length) {
            errors = {...errors, charting: 'Please choose your charting system'};
        }
        if (!software.value.length) {
            errors = {...errors, software: 'Please choose your software'};
        }
        if (!scentFreePolicy.value.length) {
            errors = {...errors, scentFreePolicy: 'Please choose your scent-free policy'};
        }

        if(Object.values(errors).find((err: any) => !!err.length)) {
            setErrorMessage(errors as ErrorProps);
            signup.error('validation_failed');
            return setScreenError(true);
        } else {
            setErrorMessage(undefined);
            setScreenError(false);
        }

        await completeRegistration({
            primaryContact: primaryContact.value,
            parking: parking.value,
            radiography: radiography.value,
            ultrasonic: ultrasonic.value,
            avgApptTime: avgApptTime.value,
            charting: charting.value,
            software: software.value,
            scentFreePolicy: scentFreePolicy.value,
        })
    };

    const onBack = () => {
        try {
            history.push({
                pathname: 'SignUp1_DO'
            }, state)
        } catch (e: any) {
            console.log('CANNOT BACK?', e)
        }
    }

    const [changeRoleLoading, , changeRole] = useActionApi({
        apiMethod: Api.changeRole,
        onError: (error) => signup.error(signupErrorReason(error)),
        onSuccess: () => {
            signup.switched('talent');
            if (safeSessionStorage.getItem('hygienistId')) {
                history.push({pathname: 'SignUp2_Talent'})
            } else {
                history.push({pathname: 'SignUp1_Talent'})
            }
        },
    });

    const changeRoleToTalent = async () => {
        TempStars.Analytics.track('Select-Route', {
            route_selected: 'UserToTalent'
        });
        await changeRole(null);
    }

    return (
        <Page>
            <Container>
                <HeaderStatusBar onButton={onBack} buttonText={'Back'} withArrow />
                <div className={"bodySignUp"}>
                    <ChangeRoleBlock><a onClick={changeRoleToTalent}>Not a dental office? Find temping and job opportunities for dental professionals here</a></ChangeRoleBlock>
                    <SignUpHeader
                        title={'Dental Office'}
                        description={'Office Details: Step 3 of 3'}
                        error={screenError ? 'Please fix the errors below' : undefined}
                    />

                    <form id="signup-form" data-testid="signup-office-details-form" className="content-block contentBlock" onSubmit={onSubmit}>
                        <div className="list-block inset inputBox inputBlock">
                            <MaterialInput
                                label="Primary Contact's Full Name"
                                type={"text"}
                                id={'primaryContact'}
                                name={"practiceName"}
                                error={!!errorMessage?.primaryContact}
                                helperText={!!errorMessage?.primaryContact && errorMessage.primaryContact}
                            />
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'parking'}
                                name={'parking'}
                                label={'Parking Situation'}
                                errorMessage={errorMessage?.parking}
                            >
                                <MenuItem value={'free-on-site'}>Free on-site</MenuItem>
                                <MenuItem value={'temp-pays'}>Temp pays for parking</MenuItem>
                                <MenuItem value={'street'}>Street</MenuItem>
                                <MenuItem value={'public-lot'}>Pay Parking @ Public Lot</MenuItem>
                                <MenuItem value={'no-parking'}>No parking available</MenuItem>
                            </MaterialSelector>
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'radiography'}
                                name={'radiography'}
                                label={'Radiography Type'}
                                errorMessage={errorMessage?.radiography}
                            >
                                <MenuItem value={'film'}>Film</MenuItem>
                                <MenuItem value={'digital'}>Digital sensors</MenuItem>
                                <MenuItem value={'phosphor-plates'}>Phosphor plates</MenuItem>
                            </MaterialSelector>
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'ultrasonic'}
                                name={'ultrasonic'}
                                label={'Ultrasonic Type'}
                                errorMessage={errorMessage?.ultrasonic}
                            >
                                <MenuItem value={'cavitron'}>Cavitron</MenuItem>
                                <MenuItem value={'piezo'}>Piezo</MenuItem>
                                <MenuItem value={'both'}>Both</MenuItem>
                                <MenuItem value={'none'}>None</MenuItem>
                            </MaterialSelector>
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'avgApptTime'}
                                name={'avgApptTime'}
                                label={'Adult Recall Time'}
                                errorMessage={errorMessage?.avgApptTime}
                            >
                                <MenuItem value={'30min'}>30min</MenuItem>
                                <MenuItem value={'40min'}>40min</MenuItem>
                                <MenuItem value={'45min'}>45min</MenuItem>
                                <MenuItem value={'50min'}>50min</MenuItem>
                                <MenuItem value={'60min'}>60min</MenuItem>
                                <MenuItem value={'70min'}>70min</MenuItem>
                            </MaterialSelector>
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'charting'}
                                name={'charting'}
                                label={'Charting System'}
                                errorMessage={errorMessage?.charting}
                            >
                                <MenuItem value={'paper charts'}>Paper charts</MenuItem>
                                <MenuItem value={'digital charts'}>Digital charts</MenuItem>
                            </MaterialSelector>
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'software'}
                                name={'software'}
                                label={'Dental Software'}
                                errorMessage={errorMessage?.software}
                            >
                                {softwareOptions.map((software) => (
                                    <MenuItem key={software} value={software}>{software}</MenuItem>
                                ))}
                            </MaterialSelector>
                        </div>

                        <div className="list-block inset inputBox">
                            <MaterialSelector
                                id={'scentFreePolicy'}
                                name={'scentFreePolicy'}
                                label={'Scent-Free Policy'}
                                errorMessage={errorMessage?.scentFreePolicy}
                            >
                                {SCENT_POLICY_OPTIONS.map(option => (
                                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                                ))}
                            </MaterialSelector>
                        </div>

                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            id="signup-button"
                            className="bigButtonInNewStyle"
                            type={'submit'}
                        >
                            Finish Sign Up
                        </Button>
                    </form>
                </div>
                <div className="footer">
                </div>
            </Container>
            <FixedLoader loading={changeRoleLoading }/>
        </Page>
    )
}
